import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, X } from 'lucide-react';
import { useDocumentosLegales, DocumentoLegal } from '@/hooks/useDocumentosLegales';

interface SubirDocumentoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubirDocumentoModal = ({ isOpen, onClose }: SubirDocumentoModalProps) => {
  const { uploadDocumento, uploading } = useDocumentosLegales();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nombreDocumento, setNombreDocumento] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState<DocumentoLegal['tipo_documento'] | ''>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Solo se permiten archivos PDF');
        return;
      }
      setSelectedFile(file);
      // Auto-fill document name with file name (without extension)
      const nameWithoutExtension = file.name.replace(/\.pdf$/i, '');
      setNombreDocumento(nameWithoutExtension);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile || !nombreDocumento.trim() || !tipoDocumento) {
      alert('Por favor completa todos los campos');
      return;
    }

    const success = await uploadDocumento(selectedFile, nombreDocumento.trim(), tipoDocumento);
    
    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setNombreDocumento('');
    setTipoDocumento('');
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Subir Documento Legal
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file-upload">Archivo PDF</Label>
            <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText className="w-8 h-8 text-red-500 mx-auto" />
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Remover
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    Haz clic para seleccionar un archivo PDF
                  </p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    Seleccionar Archivo
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Document Name */}
          <div className="space-y-2">
            <Label htmlFor="nombre-documento">Nombre del Documento</Label>
            <Input
              id="nombre-documento"
              value={nombreDocumento}
              onChange={(e) => setNombreDocumento(e.target.value)}
              placeholder="Ingresa el nombre del documento"
              required
            />
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label htmlFor="tipo-documento">Tipo de Documento</Label>
            <Select value={tipoDocumento} onValueChange={(value) => setTipoDocumento(value as DocumentoLegal['tipo_documento'])}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo de documento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Autos de Seguimientos">Autos de Seguimientos</SelectItem>
                <SelectItem value="Actas de Oralidad de Seguimiento">Actas de Oralidad de Seguimiento</SelectItem>
                <SelectItem value="Resoluciones">Resoluciones</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!selectedFile || !nombreDocumento.trim() || !tipoDocumento || uploading}
              className="flex-1"
            >
              {uploading ? 'Subiendo...' : 'Subir Documento'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubirDocumentoModal;